/**
 * @module-isolated analytics-config
 * Estado real de integraciones vía getIntegrationsStatus (analytics-api).
 * Sin exponer secretos.
 */
import {
  ensureAnalyticsCacheSheets,
  fetchAnalyticsSheetCacheStatus,
  fetchIntegrationsStatus,
  installAnalyticsCacheTrigger,
  refreshAnalyticsSheetCache
} from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';

export async function fetchConfigSnapshot() {
  const token = await getIdToken(false);
  let gas = null;
  let gasError = '';
  let cacheStatus = null;
  let cacheError = '';
  try {
    gas = await fetchIntegrationsStatus(token);
  } catch (err) {
    gasError = (err && err.message) || String(err);
  }
  try {
    cacheStatus = await fetchAnalyticsSheetCacheStatus(token);
  } catch (err) {
    cacheError = (err && err.message) || String(err);
  }

  const integrations = (gas && Array.isArray(gas.integrations) && gas.integrations.length
    ? gas.integrations
    : [
        {
          name: 'Google Apps Script (analytics-api)',
          status: gasError ? 'Error' : 'Pendiente',
          detail: gasError || 'No se pudo consultar getIntegrationsStatus.'
        }
      ]
  ).slice();

  const meta = integrations.find(function (i) {
    return /Meta Ads/i.test(i.name || '');
  });
  const sheets = integrations.find(function (i) {
    return i.name === 'Google Sheets';
  });
  const kommo = integrations.find(function (i) {
    return /Kommo/i.test(i.name || '');
  });
  const cacheInt = integrations.find(function (i) {
    return /Analytics Cache/i.test(i.name || '');
  });

  const syncJobs = [];
  if (cacheStatus && cacheStatus.success !== false) {
    syncJobs.push({
      name: 'Caché dashboards (planilla Analytics)',
      lastRun: cacheStatus.lastRefreshAt || '—',
      nextRun: cacheStatus.trigger && cacheStatus.trigger.installed ? 'cada 30 min' : 'Trigger no instalado',
      duration: cacheStatus.lastRefreshMs ? cacheStatus.lastRefreshMs + ' ms' : '—',
      records: (cacheStatus.lastRefreshOk || '0') + ' ok / ' + (cacheStatus.lastRefreshErrors || '0') + ' err',
      status: cacheStatus.lastRefreshStatus || (cacheStatus.openOk ? 'Listo' : 'Sin acceso'),
      errors: cacheStatus.openError || '—'
    });
  } else if (cacheError) {
    syncJobs.push({
      name: 'Caché dashboards (planilla Analytics)',
      lastRun: '—',
      nextRun: '—',
      duration: '—',
      records: '—',
      status: 'Error',
      errors: cacheError
    });
  }

  return {
    integrations: integrations,
    sources: [
      {
        name: 'Sales Analytics / CRM mirror',
        type: 'GAS + Sheets + Kommo',
        status:
          sheets && /Conectado/i.test(sheets.status)
            ? 'Activa (Leads)'
            : (sheets && sheets.status) || 'Pendiente',
        lastSync: (kommo && kommo.lastSync) || 'Ver sync en Leads',
        records: '—',
        errors: (sheets && /Error/i.test(sheets.status) && sheets.detail) || '—'
      },
      {
        name: 'Meta Ads',
        type: 'Marketing API (live)',
        status: (meta && meta.status) || 'No conectada',
        lastSync: (meta && meta.lastSync) || '—',
        records: /Conectado/i.test((meta && meta.status) || '') ? 'Live' : 'Sin datos',
        errors: (meta && /Error|Pendiente/i.test(meta.status) && meta.detail) || '—'
      },
      {
        name: 'Analytics Cache (_GICAPITAL - Analytics)',
        type: 'Sheets materializado',
        status: (cacheInt && cacheInt.status) || (cacheStatus && cacheStatus.openOk ? 'Conectado' : 'Pendiente'),
        lastSync: (cacheStatus && cacheStatus.lastRefreshAt) || '—',
        records: cacheStatus && cacheStatus.index ? String(cacheStatus.index.length) + ' claves' : '—',
        errors: (cacheStatus && cacheStatus.openError) || cacheError || '—'
      }
    ],
    users: [],
    roles: [
      'Administrador',
      'Gerencia',
      'Marketing',
      'Comercial',
      'Ejecutivo',
      'Solo lectura'
    ],
    syncJobs: syncJobs,
    cacheStatus: cacheStatus,
    cacheError: cacheError || null,
    logs: [],
    requiredProperties: (gas && gas.requiredProperties) || null,
    system: {
      version: '0.8.0-platform',
      environment: typeof window !== 'undefined' ? window.location.hostname : '—',
      deployHint: 'hosting:analytics',
      modules: [
        'Resumen',
        'Leads',
        'Comercial',
        'Marketing',
        'Proyectos',
        'Reuniones',
        'Reservas',
        'Ejecutivos',
        'Config'
      ],
      generatedAt: (gas && gas.generatedAt) || new Date().toISOString(),
      gasError: gasError || null
    }
  };
}

export async function runEnsureCacheSheets(opts) {
  const token = await getIdToken(false);
  return ensureAnalyticsCacheSheets(token, opts);
}

export async function runRefreshSheetCache(opts) {
  const token = await getIdToken(false);
  return refreshAnalyticsSheetCache(token, opts);
}

export async function runInstallCacheTrigger() {
  const token = await getIdToken(false);
  return installAnalyticsCacheTrigger(token);
}

export async function reloadCacheStatus() {
  const token = await getIdToken(false);
  return fetchAnalyticsSheetCacheStatus(token);
}
